import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./OverairPaywall.module.scss"

/**
 * OverairPaywall (`/overair`) — a self-contained FEATURE-SPECIFIC paywall for the
 * real Premium **Wireless Transfer** feature of Private Photo Vault (move photos
 * between your computer and your vault over your own Wi-Fi — no cable, and never
 * through anyone else's cloud).
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/overair.md
 *
 * The six prior feature concepts each perform a different real feature — Caught
 * (break-in alerts), Recall (smart search), Reflex (face-down lock), Slipstream
 * (private browser web-capture), Safety Net (trash recovery), Glyph (pattern
 * lock). This is Wireless Transfer, and it is none of them on any axis:
 *   - Feature:     Wireless Transfer — a private, local, cable-free air-link
 *                  between your computer and your vault. (Untouched by all priors.)
 *   - Layout:      a TWO-FACING-DEVICES-ACROSS-AN-AIR-GAP bridge — an exposed
 *                  computer "Pictures" folder up top, a concentric-arc SIGNAL
 *                  FIELD + vertical link track in the middle, the encrypted vault
 *                  below. No prior used a device↔device airwave bridge (Everywhere
 *                  was an isometric multi-node cloud-sync network; Slipstream was a
 *                  browser-window capture-chute from the open web).
 *   - Interaction: DRAW THE LINK — drag a signal node down the gap from computer
 *                  to vault; rings light and a waveform locks; on snap-lock the
 *                  photos AIRLIFT across the gap into the vault (dissolve off the
 *                  computer, a beam pulses down the track, each vault slot pops in).
 *                  Tap "Open the link" or keyboard also complete it. First
 *                  connect-two-points / draw-a-link mechanic.
 *   - Register:    unburdened / clever-relief — "cut the cord; bring them home."
 *                  Not fear, luxury, cozy-nostalgia, playful-loud or retro-mechanical.
 *   - Art:         LIGHT + PHOTOGRAPHIC (bundled `public/byheart/` memories);
 *                  brand blue = the private / linked / vault state, amber = the
 *                  un-linked "searching" / exposed-on-the-computer state. The
 *                  signal field + waveform are SVG. Photos stay full colour.
 *   - Value:      PRIVATE WIRELESS IMPORT / migration — "get your photos off the
 *                  shared computer and into the vault, over your own Wi-Fi, no
 *                  cable, no cloud in the middle" — via contrast framing (exposed
 *                  desktop vs sealed vault) + effort-removal. Distinct from sync
 *                  (Everywhere), web-capture (Slipstream), backup (Kept/Receipt)
 *                  and storage (Featherweight).
 *
 * Honest & App Store compliant: Wireless Transfer is a real Premium feature.
 * Purchase is never gated on the demo (tap/keyboard always complete it, and it
 * replays). No fake countdown / scarcity; the savings figure is exact yearly-vs-
 * monthly arithmetic. Yearly is the featured, default plan and carries the most
 * prominent price; monthly is small and secondary. Nothing is deleted and the copy
 * says so. Photo tiles are bundled demo memories from `public/byheart/` (no
 * network egress); "no cloud in the middle" is honest for a local Wi-Fi transfer.
 */

// nine bundled demo memories: the computer folder, and the vault they land in
const TILES = [
  "m04",
  "m12",
  "m06",
  "m16",
  "m08",
  "m11",
  "m13",
  "m18",
  "m07",
].map((s) => `/byheart/${s}.jpg`)

const STEP_MS = 235 // gap between photos as they airlift
const PULSE_MS = 360 // beam travel time down the track

type Phase = "idle" | "linked" | "transferring" | "done"
type PlanKey = "yearly" | "monthly"

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

const Wifi = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M2.5 8.5C8 4 16 4 21.5 8.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
    />
    <path
      d="M5.7 12.2C9.4 9.2 14.6 9.2 18.3 12.2"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
    />
    <path
      d="M8.9 15.8C11 14.2 13 14.2 15.1 15.8"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
    />
    <circle cx={12} cy={19.2} r={1.5} fill="currentColor" />
  </svg>
)

const Tick = ({ size = 12 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Lock = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <rect
      x={4.5}
      y={10.5}
      width={15}
      height={10}
      rx={2.6}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
    />
    <path
      d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
    <circle cx={12} cy={15.2} r={1.5} fill="currentColor" />
  </svg>
)

const CloudSlash = ({ size = 15 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M7 18h9.5a3.5 3.5 0 0 0 .4-6.98A5 5 0 0 0 8.2 9.1"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 18a3 3 0 0 1 1.4-5.66"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
    />
    <path
      d="M4 4l16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
    />
  </svg>
)

// a jagged "searching" waveform vs a clean locked sine, drawn in a 120x28 box
const NOISE_WAVE =
  "M0 14 L8 9 L14 19 L22 6 L30 21 L38 11 L46 16 L54 5 L62 22 L70 10 L78 18 L86 8 L94 20 L102 12 L110 15 L120 14"
const SINE_WAVE = "M0 14 Q10 2 20 14 T40 14 T60 14 T80 14 T100 14 T120 14"

const OverairPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── link + transfer state ───────────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>("idle")
  const [progress, setProgress] = useState(0) // 0..1 down the link track
  const [dragging, setDragging] = useState(false)
  const [moved, setMoved] = useState(0) // how many photos have landed
  const [pulses, setPulses] = useState<number[]>([]) // active beam-pulse keys
  const [ghost, setGhost] = useState(0) // teaching ghost node position 0..1
  const [demoDone, setDemoDone] = useState(false)

  const trackRef = useRef<HTMLDivElement>(null)
  const progressRef = useRef(0)
  const rafRef = useRef<number | null>(null)
  const timersRef = useRef<number[]>([])
  const pulseKeyRef = useRef(0)

  const linked =
    phase === "linked" || phase === "transferring" || phase === "done"
  const done = phase === "done"

  useEffect(() => {
    progressRef.current = progress
  }, [progress])

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((t) => window.clearTimeout(t))
    timersRef.current = []
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  useEffect(() => () => clearTimers(), [clearTimers])

  // Teaching ghost: a signal node glides ~40% down the gap and fades, once.
  useEffect(() => {
    if (reduceMotion) {
      setDemoDone(true)
      return
    }
    let cancelled = false
    const start = window.setTimeout(() => {
      const t0 = performance.now()
      const dur = 1100
      const tick = (now: number) => {
        if (cancelled) return
        const k = clamp((now - t0) / dur, 0, 1)
        // ease out then settle back toward 0 so it reads as a hint, not a commit
        const eased = k < 0.7 ? (k / 0.7) * 0.42 : 0.42 * (1 - (k - 0.7) / 0.3)
        setGhost(eased)
        if (k < 1) {
          rafRef.current = requestAnimationFrame(tick)
        } else {
          setGhost(0)
          setDemoDone(true)
        }
      }
      rafRef.current = requestAnimationFrame(tick)
    }, 700)
    return () => {
      cancelled = true
      window.clearTimeout(start)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [reduceMotion])

  // ── the airlift: photos leave the computer, beam down, land in the vault ─────
  const runTransfer = useCallback(() => {
    setPhase("transferring")
    native.logMixpanelEvent("html_overair_linked", {})

    if (reduceMotion) {
      setMoved(TILES.length)
      setPhase("done")
      native.logMixpanelEvent("html_overair_transferred", {
        count: TILES.length,
      })
      return
    }

    TILES.forEach((_, i) => {
      const t = window.setTimeout(() => {
        // fire a beam pulse down the track
        const key = ++pulseKeyRef.current
        setPulses((p) => [...p, key])
        const clean = window.setTimeout(() => {
          setPulses((p) => p.filter((k) => k !== key))
        }, PULSE_MS + 60)
        timersRef.current.push(clean)
        // the photo lands as the pulse arrives
        const land = window.setTimeout(() => {
          setMoved((m) => m + 1)
          if (i === TILES.length - 1) {
            const fin = window.setTimeout(() => {
              setPhase("done")
              native.logMixpanelEvent("html_overair_transferred", {
                count: TILES.length,
              })
            }, 260)
            timersRef.current.push(fin)
          }
        }, PULSE_MS)
        timersRef.current.push(land)
      }, i * STEP_MS)
      timersRef.current.push(t)
    })
  }, [reduceMotion])

  const lockLink = useCallback(() => {
    clearTimers()
    setGhost(0)
    setDemoDone(true)
    setProgress(1)
    setPhase("linked")
    // brief beat on the lock, then start moving photos
    const t = window.setTimeout(runTransfer, 420)
    timersRef.current.push(t)
  }, [clearTimers, runTransfer])

  // client Y → progress along the track
  const yToProgress = (clientY: number) => {
    const el = trackRef.current
    if (!el) return 0
    const r = el.getBoundingClientRect()
    return clamp((clientY - r.top) / r.height, 0, 1)
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (linked) return
    setGhost(0)
    setDemoDone(true)
    setDragging(true)
    setProgress(yToProgress(e.clientY))
    trackRef.current?.setPointerCapture?.(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging || linked) return
    setProgress(yToProgress(e.clientY))
  }

  const endDrag = () => {
    if (!dragging) return
    setDragging(false)
    if (progressRef.current >= 0.88) {
      lockLink()
    } else {
      // ease back to the top — it didn't reach the vault
      const from = progressRef.current
      const t0 = performance.now()
      const dur = 260
      const tick = (now: number) => {
        const k = clamp((now - t0) / dur, 0, 1)
        setProgress(from * (1 - k))
        if (k < 1) rafRef.current = requestAnimationFrame(tick)
      }
      rafRef.current = requestAnimationFrame(tick)
    }
  }

  // tap / keyboard: auto-drive the node down, then lock (the accessible path)
  const openLinkAuto = () => {
    if (linked) return
    if (reduceMotion) {
      lockLink()
      return
    }
    clearTimers()
    setGhost(0)
    setDemoDone(true)
    const t0 = performance.now()
    const dur = 520
    const tick = (now: number) => {
      const k = clamp((now - t0) / dur, 0, 1)
      setProgress(k)
      if (k < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        lockLink()
      }
    }
    rafRef.current = requestAnimationFrame(tick)
  }

  const resetLink = () => {
    clearTimers()
    setPulses([])
    setMoved(0)
    setProgress(0)
    setPhase("idle")
    native.logMixpanelEvent("html_overair_reset", {})
  }

  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }

  const handleClose = () => native.postNativeEvent("close")

  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  // ── derived display ──────────────────────────────────────────────────────────
  const nodePos = linked ? 1 : dragging || progress > 0 ? progress : ghost
  const strength = clamp(Math.round(nodePos * 100), 0, 100)

  const statusLabel = done
    ? "Linked · transfer complete"
    : phase === "transferring"
      ? "Sending over Wi-Fi…"
      : linked
        ? "Linked over Wi-Fi"
        : nodePos > 0.05
          ? `Finding your vault… ${strength}%`
          : "Searching for your vault…"

  const headline = done
    ? "Home. Off the computer, into the vault."
    : "Bring them home — over the air."

  const hintText = linked
    ? done
      ? `${moved} photos moved over your own Wi-Fi — no cable, no cloud.`
      : "Linked. Your photos are crossing the air…"
    : "Drag the signal down to your vault — or tap Open the link."

  const ctaLabel =
    plan === "yearly"
      ? hasTrial
        ? `Start ${trialDays} days free`
        : "Unlock Wireless Transfer"
      : "Choose monthly"

  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `${trialDays} days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  return (
    <div className={styles.page}>
      <div className={styles.grain} aria-hidden />

      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.brandKey} aria-hidden>
            <Wifi size={14} />
          </span>
          <span>Private Photo Vault</span>
          <span className={styles.premiumPip}>Premium</span>
        </div>
        <h1 className={styles.title}>{headline}</h1>
        <p className={styles.sub}>
          Your photos sit in a folder on the computer, where anyone at the desk
          can open them. <b>Wireless Transfer</b> moves them straight into your
          vault over your own Wi-Fi — no cable, and never through anyone else's
          cloud.
        </p>
      </header>

      {/* ── The air-gap bridge ─────────────────────────────────────────────── */}
      <div className={`${styles.stage} ${linked ? styles.stageLinked : ""}`}>
        {/* computer: an exposed "Pictures" folder */}
        <div className={styles.device}>
          <div className={styles.deviceBar}>
            <span className={styles.dots} aria-hidden>
              <i />
              <i />
              <i />
            </span>
            <span className={styles.deviceName}>This computer · Pictures</span>
            <span
              className={`${styles.exposedTag} ${
                done ? styles.exposedTagCleared : ""
              }`}
            >
              {done ? "Cleared" : "Open to anyone here"}
            </span>
          </div>
          <div className={styles.compGrid} aria-hidden>
            {TILES.map((src, i) => {
              const sent = i < moved
              return (
                <span
                  key={i}
                  className={`${styles.compCell} ${sent ? styles.compSent : ""}`}
                >
                  {!sent && (
                    <img
                      src={src}
                      alt=""
                      draggable={false}
                      onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).style.display =
                          "none"
                      }}
                    />
                  )}
                </span>
              )
            })}
          </div>
        </div>

        {/* the signal field + draggable link track */}
        <div
          ref={trackRef}
          className={`${styles.field} ${dragging ? styles.fieldDrag : ""}`}
          role="button"
          tabIndex={0}
          aria-label="Open a private Wi-Fi link from the computer to your vault"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              if (!linked) openLinkAuto()
            }
          }}
        >
          {/* concentric arcs radiating from both devices */}
          <svg
            className={styles.rings}
            viewBox="0 0 120 120"
            preserveAspectRatio="none"
            aria-hidden
          >
            {[0, 1, 2].map((r) => (
              <circle
                key={`t${r}`}
                className={styles.ring}
                cx={60}
                cy={-6}
                r={26 + r * 20}
                style={{ opacity: linked ? 0.5 - r * 0.13 : 0.28 - r * 0.08 }}
              />
            ))}
            {[0, 1, 2].map((r) => (
              <circle
                key={`b${r}`}
                className={styles.ring}
                cx={60}
                cy={126}
                r={26 + r * 20}
                style={{ opacity: linked ? 0.5 - r * 0.13 : 0.28 - r * 0.08 }}
              />
            ))}
          </svg>

          {/* the dashed link line the node travels */}
          <div className={styles.trackLine} aria-hidden>
            <span
              className={styles.trackFill}
              style={{ height: `${nodePos * 100}%` }}
            />
          </div>

          {/* beam pulses that travel down as photos cross */}
          {pulses.map((k) => (
            <span key={k} className={styles.pulse} aria-hidden />
          ))}

          {/* the signal node you drag */}
          <span
            className={`${styles.node} ${linked ? styles.nodeLinked : ""} ${
              ghost > 0 && !dragging && progress === 0 ? styles.nodeGhost : ""
            }`}
            style={{ top: `${nodePos * 100}%` }}
            aria-hidden
          >
            <span className={styles.nodeCore}>
              {linked ? <Lock size={15} /> : <Wifi size={16} />}
            </span>
          </span>

          {/* the status pill + waveform, centred in the gap */}
          <div className={styles.statusPill}>
            <svg
              className={styles.wave}
              viewBox="0 0 120 28"
              preserveAspectRatio="none"
              aria-hidden
            >
              <path
                className={`${styles.waveLine} ${
                  linked ? styles.waveLocked : ""
                }`}
                d={linked ? SINE_WAVE : NOISE_WAVE}
              />
            </svg>
            <span className={styles.statusText}>{statusLabel}</span>
          </div>
        </div>

        {/* the encrypted vault the photos land in */}
        <div className={`${styles.vault} ${done ? styles.vaultSealed : ""}`}>
          <div className={styles.deviceBar}>
            <span className={styles.vaultLock} aria-hidden>
              <Lock size={13} />
            </span>
            <span className={styles.deviceName}>Your vault · Encrypted</span>
            <span className={styles.movedCount}>
              {moved} photo{moved === 1 ? "" : "s"} moved
            </span>
          </div>
          <div className={styles.vaultGrid} aria-hidden>
            {TILES.map((src, i) => {
              const arrived = i < moved
              return (
                <span
                  key={i}
                  className={`${styles.vaultCell} ${
                    arrived ? styles.vaultArrived : ""
                  }`}
                >
                  {arrived && (
                    <img
                      src={src}
                      alt=""
                      draggable={false}
                      onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).style.display =
                          "none"
                      }}
                    />
                  )}
                </span>
              )
            })}
          </div>
        </div>
      </div>

      <p
        className={`${styles.hint} ${linked ? styles.hintOn : ""}`}
        aria-live="polite"
      >
        {hintText}
      </p>

      <div className={styles.actions}>
        {!linked ? (
          <button className={styles.linkBtn} onClick={openLinkAuto}>
            <Wifi size={16} />
            Open the link over Wi-Fi
          </button>
        ) : (
          <button className={styles.ghostBtn} onClick={resetLink}>
            Send another batch
          </button>
        )}
      </div>

      {/* the route the photos take — honest "skips the cloud" proof */}
      <div className={styles.route} aria-hidden>
        <span className={styles.routeNode}>Computer</span>
        <span className={styles.routeArrow}>→</span>
        <span className={`${styles.routeNode} ${styles.routeWifi}`}>
          <Wifi size={12} /> your Wi-Fi
        </span>
        <span className={styles.routeArrow}>→</span>
        <span className={`${styles.routeNode} ${styles.routeVault}`}>
          <Lock size={11} /> vault
        </span>
        <span className={styles.routeSkip}>
          <CloudSlash size={13} /> skips the cloud
        </span>
      </div>

      {/* what Wireless Transfer is, in the honest Premium bundle (one short list) */}
      <ul className={styles.keeps}>
        {[
          "No cable, no iCloud or Google in the middle",
          "Full-quality photos & videos, kept private",
          "Encrypted & in sync on every device",
        ].map((t) => (
          <li key={t}>
            <span className={styles.keepDot} aria-hidden>
              <Tick size={10} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      {/* ── Plan choice — yearly featured, monthly small & secondary ─────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${
            plan === "yearly" ? styles.planSel : ""
          }`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planMain}>
            <span className={styles.planName}>Yearly</span>
            <span className={styles.planNote}>
              Best value · the whole library, always moving
            </span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
            <span className={styles.planSmall}>
              {yearlyPerMonth}/mo
              {savingsPct > 0 ? ` · save ${savingsPct}%` : ""}
            </span>
          </span>
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.planMonthSel : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Your photos are always yours. Wireless Transfer only moves them —
        nothing is ever deleted.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default OverairPaywall
